import { SettingsIcon } from "Icons/Settings";

import { useCallback, useEffect, useRef, useState } from "react";

import { css } from "@emotion/react";

import { UpChunk } from "@mux/upchunk";
import { useRouter } from "next/router";

import { Disclaimer } from "components/shared/library/Disclaimer";
import { HelpText } from "components/shared/library/HelpText";
import { SpinningIcon } from "components/shared/library/SpinningIcon";
import { WhiteLabelButton } from "components/shared/library/WhiteLabelButton";
import { OnboardingContainer } from "components/shared/onboarding/OnboardingContainer";

import { useInterview } from "contexts/InterviewContext";
import { useAlert } from "contexts/alertContext";

import { getInterviewCallId, useCallRecruiAIAgent } from "apis/recruit";

import { useMediaRecorder } from "utils/Stream";
import { down, up } from "utils/mediaQueryStrings";
import { formatCounter } from "utils/time";
import { replaceQueryParams } from "utils/urls";
import { useWakeLock } from "utils/wakeLock";

import { uiGray, white } from "constants/colors";

import { AudioSettings } from "./AudioSettings";
import { CallEnded } from "./CallEnded";
import { CallInfo } from "./CallInfo";
import { CandidateFlowCardHeader } from "./CandidateFlowCardHeader";
import { LiveCall } from "./LiveCall";

const style = {
  card: css({
    padding: "24px 40px",
    display: "flex",
    flexDirection: "column",
    gap: 40,
    background: white,
    boxShadow: "0px 32px 64px -12px rgba(27, 27, 27, 0.14)",
    borderRadius: 16,
    maxWidth: 600,
    margin: 16,
    [down("mobile")]: {
      marginBottom: 120,
    },
    overflow: "hidden",
  }),
  fixedWidth: css({
    [up("mobile")]: {
      width: 500,
    },
  }),
  section: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
  }),
  scrollSettings: css({
    display: "flex",
    flexDirection: "column",
    gap: 40,
    flex: 1,
    overflow: "auto",
  }),
  callInfo: css({
    display: "flex",
    flexDirection: "column",
    gap: 8,
    width: "-webkit-fill-available",
  }),
  centerAlign: css({ textAlign: "center" }),
  buttons: css({
    display: "flex",
    flexDirection: "column",
    width: "-webkit-fill-available",
    gap: 8,
    flexShrink: 0,
  }),
  shareInterview: css({
    padding: 24,
    border: `1px solid ${uiGray[10]}`,
    borderRadius: 16,
  }),
};

type CallState =
  | "not-started"
  | "settings"
  | "count-down"
  | "ongoing"
  | "ended";

type Props = { onGoToSettings?: () => Promise<void>; isDemo?: boolean };
export const CallStep = ({ onGoToSettings, isDemo }: Props) => {
  const [accessToken, setAccessToken] = useState<string>();
  const [mediaUploadUrl, setMediaUploadUrl] = useState<string>();
  const [audioStream, setAudioStream] = useState<MediaStream>();
  const [videoStream, setVideoStream] = useState<MediaStream>();
  const [sinkId, setSinkId] = useState<string>();
  const [callState, setCallState] = useState<CallState>("not-started");
  const [countDown, setCountDown] = useState(5);
  const [secondsIn, setSecondsIn] = useState(0);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoPaused, setIsVideoPaused] = useState(false);
  const { createAlert } = useAlert();
  const router = useRouter();

  const countUpId = useRef<NodeJS.Timer>();

  const { startCall, endCall, callStatus, isAgentTalking, mute, unmute } =
    useCallRecruiAIAgent();

  const { isVideoEnabled, interviewFlowId } = useInterview();
  const [isVideoUploading, setIsVideoUploading] = useState(isVideoEnabled);
  // Prevent screen lock during a call
  useWakeLock(callStatus == "ongoing", callStatus == "ended");

  const uploadVideoToMux = useCallback(
    (file: File) => {
      if (isDemo)
        return setTimeout(() => {
          setIsVideoUploading(false);
        }, 5000);
      if (mediaUploadUrl) {
        const upload = UpChunk.createUpload({
          endpoint: mediaUploadUrl,
          file,
          chunkSize: 5120, // Uploads the file in ~5mb chunks
        });
        upload.on("success", async () => setIsVideoUploading(false));
      }
    },
    [mediaUploadUrl, isDemo]
  );

  const { mediaRecorder, error: mediaRecorderError } = useMediaRecorder({
    stream: videoStream,
    onRecordedMediaReady: uploadVideoToMux,
  });

  useEffect(() => {
    let countDownId: NodeJS.Timer;

    if (callState === "count-down" && accessToken) {
      // During the count down step we count down
      countDownId = setInterval(() => {
        setCountDown((state) => {
          if (state === 0) {
            clearInterval(countDownId);
            startCall(accessToken, audioStream, sinkId);
            // start the video recording
            mediaRecorder?.start();
            return 0;
          }
          return state - 1;
        });
      }, 1000);
    }
    if (callState === "ongoing") {
      // During the ongoing step we count up the duration of ongoing call
      countUpId.current = setInterval(() => {
        setSecondsIn((state) => state + 1);
      }, 1000);
    }
    if (callState === "ended") {
      // Once the call ended, we stop te count up
      clearInterval(countUpId.current);
    }

    return () => {
      if (countDownId) clearInterval(countDownId);
    };
  }, [callState, accessToken, startCall, audioStream, sinkId, mediaRecorder]);

  useEffect(() => {
    if (callStatus === "ongoing") setCallState("ongoing");
    if (callStatus === "ended") {
      // stop the streams
      audioStream?.getTracks().forEach((track) => track.stop());
      videoStream?.getTracks().forEach((track) => track.stop());
      mediaRecorder?.stop();
      if (isDemo) replaceQueryParams(router, "step", "demo-completed");
      else setCallState("ended");
    }
  }, [callStatus, audioStream, videoStream, mediaRecorder, router, isDemo]);

  return (
    <OnboardingContainer>
      {callState === "not-started" && (
        <div css={style.card}>
          <CallInfo
            isDemo={isDemo}
            onClick={async () => {
              if (onGoToSettings) await onGoToSettings();
              setCallState("settings");
            }}
          />
        </div>
      )}
      {callState === "settings" && (
        <div css={style.card}>
          <CandidateFlowCardHeader
            Icon={SettingsIcon}
            title={
              isVideoEnabled
                ? "Video and audio check"
                : "Double check your audio"
            }
            subtitle={`Before you start, make sure your ${
              isVideoEnabled ? "video and " : ""
            }audio is set up properly.`}
          />
          <div css={style.scrollSettings}>
            <AudioSettings
              onAudioStreamChange={setAudioStream}
              audioStream={audioStream}
              onVideoStreamChange={setVideoStream}
              videoStream={videoStream}
              onSinkIdChange={setSinkId}
              sinkId={sinkId}
            />
          </div>

          <div css={style.buttons}>
            {isVideoEnabled && <HelpText>{mediaRecorderError}</HelpText>}
            <WhiteLabelButton
              isFullWidth
              // we should not start the call without a audioStream
              // we should not start the call if video is enabled and there is no videoStream
              // we should not start the call if video is enabled and there is no uploadDetail
              disabled={
                !audioStream ||
                (isVideoEnabled && (!videoStream || !!mediaRecorderError))
              }
              onClick={async () => {
                const res = await getInterviewCallId({
                  interviewFlowId,
                  isDemo,
                });
                if (
                  res.error ||
                  !res.data?.accessToken ||
                  (isVideoEnabled && !res.data?.mediaUploadUrl)
                ) {
                  createAlert(
                    "error",
                    "Something when wrong when initiating the call"
                  );
                } else {
                  setAccessToken(res.data?.accessToken);
                  setMediaUploadUrl(res.data?.mediaUploadUrl);

                  setCallState("count-down");
                }
              }}
            >
              Start interview
            </WhiteLabelButton>
          </div>
        </div>
      )}
      {callState === "count-down" && (
        <div css={style.card}>
          <CandidateFlowCardHeader
            Icon={SpinningIcon}
            title={`Starts in... ${countDown}`}
            subtitle={"Your interview is starting soon."}
          />
          {isVideoEnabled && (
            <Disclaimer
              type={"info"}
              title={
                "This interview requires you to be on camera at all times."
              }
            />
          )}
          <div css={style.buttons}>
            <WhiteLabelButton isFullWidth disabled>
              Get ready...
            </WhiteLabelButton>
          </div>
        </div>
      )}
      {callState === "ongoing" && (
        <LiveCall
          counter={formatCounter(secondsIn)}
          handleEndCall={endCall}
          videoStream={videoStream}
          audioStream={audioStream}
          isAgentTalking={isAgentTalking}
          toggleAudioMute={() => {
            if (isAudioMuted) unmute();
            else mute();
            setIsAudioMuted(!isAudioMuted);
          }}
          toggleVideoPause={() => {
            // Set each video track's enabled property to the opposite of the current paused state
            videoStream?.getVideoTracks().forEach((track) => {
              track.enabled = isVideoPaused; // We're toggling from the current state, so use the inverse
            });
            setIsVideoPaused(!isVideoPaused);
          }}
          isAudioMuted={isAudioMuted}
          isVideoPaused={isVideoPaused}
        />
      )}
      {callState === "ended" && (
        <div css={[style.card, style.fixedWidth]}>
          <CallEnded
            secondsIn={secondsIn}
            isVideoUploading={isVideoUploading}
          />
        </div>
      )}
    </OnboardingContainer>
  );
};
